/**
 * @module components/TabsApi
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Request summary table
 */

import { useLanguageState } from '../contexts/language-state-provider';


/**
 * React component
 * ===============
 */
const TabsApi = () => {
  const [ lang ] = useLanguageState();

  let sharedUrl = <p>The shareable URL for this request is: <strong>{document.URL}</strong></p>
  let webserviceUrl = <p>In lieu of the online tool, the hazard values can also be requested (in json format) through a webservice using the GraphQL Application Programming Interface (API). The webservice address is: <strong>https://www.earthquakescanada.nrcan.gc.ca/api/canshm/graphql</strong></p>
  let example = 'Example'
  let example1 = <p>Request for NBCC 2025 PGA, S<sub>a</sub>(0.2) and S<sub>a</sub>(0.5) for a site designation X<sub>760</sub> (V<sub>s30</sub> value of 760 m/s) for probabilities of exceedance of 2%, 5% and 10% in 50 years for a site located at 45.421, -75.697:</p>
  let example2 = <p>Same as example 1 but for a site designation X<sub>C</sub> and for NBCC 2020.</p>
  let example3 = <p>Request NBCC 2025 seismic hazard values for a probability of exceedance of 2% in 50 years for two sites, two site designation X<sub>V</sub> (V<sub>s30</sub>'s) and for a larger range of parameters.</p>
  let example4 = <p>The API can also return information on whether the requested site is:  (1) close proximity to the East Denali and Duke River faults in northwestern British Columbia and western Yukon; (2) in the high-Arctic or (3) in Canada. Refer to the Commentary entitled Design for Seismic Effects in the <em>Structural Commentaries (User’s Guide – NBC 2020: Part 4 of Division B</em> for guidance on these cases.</p>
  let example4_text = <p>For the appropriate latitude (lat) and longitude (lon) the API will return a Zones object which will contain 'NBC_CNB2020_NW-Fault-Warning_NO-Faille-Avis’ for case 1; 'NBC_CNB2020_High-Arctic-Warning_Haut-Arctique-Avis' for case 2; and/or 'NBC_CNB2020_Canada' for case 3.</p> 

  if (lang.abbr === 'fr') {
    sharedUrl = <p>L'URL partageable pour cette requête est la suivante : <strong>{document.URL}</strong></p>
    webserviceUrl = <p>Au lieu d'utiliser l'outil en ligne, il est également possible de solliciter les valeurs d'aléas (en format JSON) par l'entremise d'un service Web utilisant l’interface de programmation d’applications (API) GraphQL. L’adresse pour le service Web est : <strong>https://www.seismescanada.rncan.gc.ca/api/canshm/graphql</strong></p>
    example = 'Exemple'
    example1 = <p>Demande de CNBC 2025 PGA, Sa(0,2) et Sa(0,5) pour une valeur V<sub>s30</sub> de 760 m/s pour des probabilités de dépassement de 2 %, 5 % et 10 % en 50 ans pour un emplacement situé à 45,421; -75,697:</p>
    example2 = <p>Identique à l'exemple 1, mais pour une désignation d'emplacement X<sub>C</sub> et pour CNBC 2020.</p>
    example3 = <p>Demande de CNBC 2025 l'aléa de 2 % en 50 ans pour deux emplacements, deux valeurs V<sub>s30</sub> et pour une gamme de paramètres plus grande.</p>
    example4 = <p>L'API peut également fournir des données indiquant si l'emplacement demandé se trouve : 1) à proximité des failles de Denali Est et de la rivière Duke dans le nord-ouest de la Colombie-Britannique et l’ouest du Yukon; 2) dans le Haut-Arctique; ou 3) à l’intérieur du Canada. Veuillez consulter le commentaire intitulé Calcul fondé sur les effets dus aux séismes du <em>Commentaires sur le calcul structures (Guide de l’utilisateur – CNB 2020 : Partie 4 de la division B)</em> pour obtenir des conseils pour ces cas.</p>
    example4_text = <p>Pour la latitude (lat) et la longitude (lon) appropriées, l'API renverra un objet Zones qui contiendra 'NBC_CNB2020_NW-Fault-Warning_NO-Faille-Avis' pour le cas 1 ; 'NBC_CNB2020_High-Arctic-Warning_Haut-Arctique-Avis' pour le cas 2 ; et/ou 'NBC_CNB2020_Canada' pour le cas 3.</p>                  
  }

  return (
    <>
      {sharedUrl}
      {webserviceUrl}
      <h3>{example} 1</h3>
      {example1}
      <pre>
      {
"query{\n\
  NBC2025(latitude: 45.421, longitude: -75.697){\n\
    X760: siteDesignationsXv(vs30: 760, poe50: [2.0, 5.0, 10.0]){\n\
      sa0p2\n\
      sa0p5\n\
    }\n\
  }\n\
}"
      }
      </pre>
      <h3>{example} 2</h3>
      {example2}
      <pre>
      {
"query{\n\
  NBC2020(latitude: 45.421, longitude: -75.697){\n\
    XC: siteDesignationsXs(siteClass: C, poe50: [2.0, 5.0, 10.0]){\n\
      sa0p2\n\
      sa0p5\n\
    }\n\
  }\n\
}"
      }
      </pre>
      <h3>{example} 3</h3>
      {example3}
      <pre>
      {
"query{\n\
  Ottawa: NBC2025(latitude: 45.42, longitude: -75.69){\n\
    X250: siteDesignationsXv(vs30: 250, poe50: [2.0]){\n\
      ...groundMotions\n\
    }\n\
    X450: siteDesignationsXv(vs30: 450, poe50: [2.0]){\n\
      ...groundMotions\n\
    }\n\
  }\n\
  Vancouver: NBC2025(latitude: 49.25, longitude: -123.12){\n\
    X250: siteDesignationsXv(vs30: 250, poe50: [2.0]){\n\
      ...groundMotions\n\
    }\n\
    X450: siteDesignationsXv(vs30: 450, poe50: [2.0]){\n\
      ...groundMotions\n\
    }\n\
  }\n\
}\n\
\n\
fragment groundMotions on CanSHM6SiteDesignation {\n\
  sa0p2\n\
  sa0p5\n\
  sa1p0\n\
  sa2p0\n\
  sa5p0\n\
  sa10p0\n\
  pga\n\
}"
      }
      </pre>
      <h3>{example} 4</h3>
      {example4}
      <pre>
      {
"query{\n\
  NBC2020(latitude: lat, longitude: lon) {\n\
    ...point\n\
  }\n\
}\n\
\n\
fragment point on CanSHM6Point {\n\
  metadata: metadata {\n\
      zones\n\
  }\n\
  X450: siteDesignationsXv(vs30: 450, poe50: [2.0]){\n\
    sa0p2\n\
  }\n\
}\n\
\n\
"
      }
      </pre>
      {example4_text}
    </>
  );
};

export default TabsApi;